/**
 * Authentication adapter.
 *
 * `clerk` mode is the formal deployment: invitation-only Clerk with Google OAuth
 * and Magic Link. `dev` mode talks to the local dev issuer instead, which mints
 * tokens the Worker verifies through the very same JWKS path. Only the token
 * source differs — never the Worker's verification or authorization.
 */
const MODE = import.meta.env.VITE_AUTH_MODE || 'dev';
const DEV_ISSUER = import.meta.env.VITE_DEV_ISSUER || 'http://127.0.0.1:8788';
const STORAGE_KEY = 'rent-notebook.dev-identity';

let cached = null;
let clerk = null;

export const authMode = MODE;

/** Whether this build actually carries a Clerk publishable key. */
export const clerkConfigured = MODE !== 'clerk' || Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

async function loadClerk() {
  if (clerk) return clerk;
  const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!key) throw new Error('缺少 VITE_CLERK_PUBLISHABLE_KEY 設定');
  const { Clerk } = await import('@clerk/clerk-js');
  clerk = new Clerk(key);
  await clerk.load({ afterSignOutUrl: window.location.origin });
  return clerk;
}

export async function currentIdentity() {
  if (MODE === 'dev') {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }
  const instance = await loadClerk();
  const user = instance.user;
  if (!user) return null;
  return {
    sub: user.id,
    email: user.primaryEmailAddress ? user.primaryEmailAddress.emailAddress : '',
    display_name: user.fullName || user.username || '',
  };
}

export async function devIdentities() {
  const response = await fetch(`${DEV_ISSUER}/identities`);
  if (!response.ok) throw new Error('無法取得本機測試身分');
  return response.json();
}

export async function signInAsDevIdentity(identity) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  cached = null;
}

export async function startClerkSignIn(strategy) {
  const instance = await loadClerk();
  instance.openSignIn({
    afterSignInUrl: window.location.href,
    afterSignUpUrl: window.location.href,
    initialValues: {},
    ...(strategy === 'magic-link' ? { appearance: {} } : {}),
  });
}

export async function signOut() {
  cached = null;
  if (MODE === 'dev') {
    window.localStorage.removeItem(STORAGE_KEY);
    window.location.assign('/');
    return;
  }
  const instance = await loadClerk();
  await instance.signOut();
}

export async function getSessionToken() {
  if (MODE === 'dev') {
    const identity = await currentIdentity();
    if (!identity) return null;
    if (cached && cached.sub === identity.sub && cached.expiresAt > Date.now() + 30_000) return cached.token;
    const response = await fetch(`${DEV_ISSUER}/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sub: identity.sub, email: identity.email }),
    });
    if (!response.ok) return null;
    const body = await response.json();
    cached = { sub: identity.sub, token: body.token, expiresAt: Date.now() + 55 * 60 * 1000 };
    return body.token;
  }
  const instance = await loadClerk();
  if (!instance.session) return null;
  return instance.session.getToken();
}
